import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import classNames from 'classnames';
import { throttle } from 'lodash-es';
import bind from 'memoize-bind';
import moment from 'moment-timezone';
import React, { Fragment, PureComponent } from 'react';

import ArrowDown from '@sbu/assets.svg/src/single-chevron-down.svg';
import { IconButton } from '@sumologic/yama-ds-components';

import css from './Time.module.scss';
import { TimeNavigation } from './TimeNavigation';

interface TimeProps {
  active?: Date | undefined;
  isMilitary?: boolean;
  includeSeconds?: boolean;
  disabled?: boolean;
  onChange: (time: Date | undefined) => void;
}

interface TimeState {
  activeIndex: number;
}

const NAVIGATION_KEYS = ['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp'];
export type UnitOfTime = 'hour' | 'minute' | 'second';

export class Time extends PureComponent<TimeProps, TimeState> {
  static defaultProps: TimeProps = {
    isMilitary: true,
    includeSeconds: false,
    disabled: false,
    onChange: () => undefined,
  };

  state: TimeState = { activeIndex: -1 };

  getActiveValue(unit: UnitOfTime) {
    const { active, isMilitary } = this.props;
    const activeMoment = moment(active);
    const hour = isMilitary ? activeMoment.hour() : activeMoment.format('h');

    if (unit === 'hour') {
      return hour;
    }

    return activeMoment[`${unit}`]();
  }

  getStandardDiff(direction: -1 | 1) {
    const { active } = this.props;
    const activeHour = moment(active).hour();

    if (direction > 0 && activeHour % 12 === 11) {
      return -11;
    } else if (direction < 0 && activeHour % 12 === 0) {
      return 11;
    } else {
      return direction;
    }
  }

  onTimepartChange(unit: UnitOfTime, direction: -1 | 1) {
    const { active, onChange, isMilitary } = this.props;
    const timeparts = { hour: 24, minute: 60, second: 60 };
    const activeMoment = moment(active);
    const activePartValue = activeMoment[`${unit}`]();
    let diff = activePartValue + direction;

    if (!isMilitary && unit === 'hour') {
      diff = this.getStandardDiff(direction);
    } else if (diff < 0) {
      diff = diff + timeparts[unit];
    } else if (diff >= timeparts[unit]) {
      diff = 1 - timeparts[unit];
    } else {
      diff = direction;
    }

    onChange(activeMoment.add(diff, unit).toDate());
  }

  onMeridiemClick(meridiem: 'AM' | 'PM') {
    const { active, onChange } = this.props;
    const currentMeridiem = moment(active).format('A');
    const offset = currentMeridiem === 'AM' ? 12 : -12;

    if (meridiem === currentMeridiem) {
      return;
    }

    onChange(moment(active).add(offset, 'hours').toDate());
  }

  onWheel(unit: UnitOfTime, event: React.WheelEvent) {
    event.persist();
    event.preventDefault();
    event.stopPropagation();

    if (!this.props.disabled) {
      this.onWheelDebounce(unit, event.nativeEvent);
    }
  }

  onWheelDebounce = throttle(
    (unit: UnitOfTime, event: React.WheelEvent['nativeEvent']) => {
      const direction = event.deltaY < 0 ? -1 : 1;
      this.onTimepartChange(unit, direction);
    },
    50
  );

  stopPropagation(event: React.KeyboardEvent) {
    if (NAVIGATION_KEYS.includes(event.key)) {
      event.stopPropagation();
    }
  }

  setFocus(activeIndex: number) {
    this.setState({ activeIndex });
  }

  renderTime(unit: UnitOfTime) {
    const { disabled } = this.props;

    return (
      <div className={css.timepart}>
        <IconButton
          aria-label={'Previous ' + unit}
          size='medium'
          disabled={disabled}
          onClick={() => this.onTimepartChange(unit, 1)}
          onKeyDown={bind(this.stopPropagation, this)}
          sx={{ m: 0.625, p: 0.625, transform: 'rotate(180deg)' }}
        >
          <ArrowDown />
        </IconButton>
        <div
          className={classNames(css.timepartActive, {
            [css.disabledWheel]: disabled,
          })}
          onWheel={(e) => this.onWheel(unit, e)}
        >
          {this.getActiveValue(unit).toString().padStart(2, '0')}
        </div>
        <IconButton
          aria-label={'Next ' + unit}
          size='medium'
          disabled={disabled}
          onClick={() => this.onTimepartChange(unit, -1)}
          onKeyDown={bind(this.stopPropagation, this)}
          sx={{ m: 0.625, p: 0.625 }}
        >
          <ArrowDown />
        </IconButton>
      </div>
    );
  }

  render() {
    const { active, isMilitary, includeSeconds, disabled } = this.props;
    const { activeIndex } = this.state;

    const time: Array<UnitOfTime> = ['hour', 'minute'];
    if (includeSeconds) {
      time.push('second');
    }

    return (
      <Fragment>
        <TimeNavigation
          activeIndex={activeIndex}
          className={css.time}
          navigationKeys={NAVIGATION_KEYS}
          onArrowUpDown={bind(this.onTimepartChange, this)}
          onIndexChange={bind(this.setFocus, this)}
        >
          {time.map((part, i) => (
            <div className={css.time} key={part}>
              {this.renderTime(part)}
              {i !== time.length - 1 && (
                <div className={css.timepartDivider}>:</div>
              )}
            </div>
          ))}
        </TimeNavigation>
        {!isMilitary && (
          <div className={css.meridiem}>
            <ToggleButtonGroup
              value={moment(active).format('A')}
              aria-label='Meridiem'
              onChange={(_e, value) =>
                value !== null && bind(this.onMeridiemClick, this)(value)
              }
              exclusive
              size='small'
            >
              <ToggleButton disabled={disabled} value='AM'>
                AM
              </ToggleButton>
              <ToggleButton disabled={disabled} value='PM'>
                PM
              </ToggleButton>
            </ToggleButtonGroup>
          </div>
        )}
      </Fragment>
    );
  }
}
